// The version-dispatched session-list cache commands, split out of
// `src/hooks/query/useQueryCombinedSessions.ts` (which re-exports them) with
// the D1 family. The `useQueryCombinedSessions` composition itself stays
// app-side — it unions offline-recording atoms into the list (the D2f
// boundary) — but these commands are pure key/version dispatch: they read
// `sessionQueryVersionAtom` (package-owned) and address the app's query
// cache through the injected `getQueryClient` seam.
//
// Store identity (the capture lesson): the non-hook commands read the atom
// via `getDefaultStore()`, exactly as the app-side originals did — pinned to
// the production provider-less store (see `../atoms/sessionAtoms.ts`).

import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_SESSIONS_WITH_PAGINATION,
  GET_INFINITE_TEAM_MEMBER_SESSIONS,
  GET_SESSIONS,
  GET_SESSIONS_LIST,
  GET_TEAM_MEMBER_SESSIONS,
  SESSION_LIST_DEFAULT_SORT_BY,
  SESSIONS_PAGE_SIZE,
  type SessionListItem,
  type SessionListResponse,
  type SimpleSessionResponse,
} from '../../contracts';
import { sessionQueryVersionAtom } from '../atoms/sessionAtoms';
import { getSessionDataConfig } from '../config';
import { invalidateSessionStatusCount } from './sessionStatusCountKeys';

type V1SessionsData = { sessions?: SimpleSessionResponse[] };
type PagedSessionsData = { pages?: Array<{ sessions?: SimpleSessionResponse[] }> };

/**
 * Invalidate sessions query cache based on current feature flags.
 * Handles V1, V2, V3 (legacy version-dispatched) AND the new flag-gated
 * `GET_SESSIONS_LIST` query used by SessionsListPanel. Resolves once active,
 * unpaused list queries have *attempted* their refetch. Not a freshness
 * guarantee: paused (offline) and disabled queries are skipped, refetch
 * errors are swallowed, and the promise never rejects.
 */
export async function invalidateSessionsQuery(): Promise<void> {
  const store = getDefaultStore();
  const sessionQueryVersion = store.get(sessionQueryVersionAtom);
  const queryClient = getSessionDataConfig().getQueryClient();

  void invalidateSessionStatusCount();

  const versionKey = (() => {
    switch (sessionQueryVersion) {
      case 'v3':
        // V3: all queries starting with GET_INFINITE_SESSIONS_WITH_PAGINATION —
        // partial match since the query key includes filters.
        return [GET_INFINITE_SESSIONS_WITH_PAGINATION];
      case 'v2':
        // V2: infinite sessions query. The original hub used a raw `100` here;
        // the contract constant is the same frozen value (`keys.frozen.test.ts`
        // pins it), single-sourced so the invalidation key cannot drift from
        // the query key it must match.
        return [GET_INFINITE_SESSIONS, SESSIONS_PAGE_SIZE];
      default:
        // V1: simple sessions query.
        return [GET_SESSIONS];
    }
  })();

  await Promise.all([
    // GET_SESSIONS_LIST is mounted in parallel with the legacy V1/V2/V3
    // path (flag-gated), so it has to be invalidated unconditionally.
    queryClient.invalidateQueries({ queryKey: [GET_SESSIONS_LIST] }),
    queryClient.invalidateQueries({ queryKey: versionKey }),
  ]);
}

/**
 * Read the first cached session id for the active query version WITHOUT
 * subscribing to the query (no fetch, no polling). Mirrors the version
 * switch in `invalidateSessionsQuery`. Returns undefined when nothing is
 * cached yet, so callers must provide a fallback.
 *
 * Lets non-scribe surfaces (e.g. the always-mounted sidebar) resolve the
 * split-view target without mounting an always-on sessions observer.
 */
export function getFirstCachedSessionId(): string | undefined {
  const store = getDefaultStore();
  const sessionQueryVersion = store.get(sessionQueryVersionAtom);
  const queryClient = getSessionDataConfig().getQueryClient();

  const firstFromPages = (data?: PagedSessionsData) =>
    data?.pages?.flatMap((page) => page.sessions ?? [])[0]?.session_id;

  switch (sessionQueryVersion) {
    case 'v3': {
      // V3/V2 keys include filters/page size, so match by prefix and read
      // the first cached query rather than reconstructing the exact key.
      const [first] = queryClient.getQueriesData<PagedSessionsData>({
        queryKey: [GET_INFINITE_SESSIONS_WITH_PAGINATION],
      });
      return firstFromPages(first?.[1]);
    }
    case 'v2': {
      const [first] = queryClient.getQueriesData<PagedSessionsData>({
        queryKey: [GET_INFINITE_SESSIONS],
      });
      return firstFromPages(first?.[1]);
    }
    default: {
      const data = queryClient.getQueryData<V1SessionsData>([GET_SESSIONS]);
      return data?.sessions?.[0]?.session_id;
    }
  }
}

/** One cached v2.2 (`GET_SESSIONS_LIST`) list query, identified by the date
 *  window and sort order in its cache key. The default list is stored as one
 *  query per date group, so a caller needs that identity to tell a complete
 *  snapshot from a partial one. */
export type CachedSessionsListWindow = {
  /** ISO lower bound from the cache key; undefined when open-ended. */
  from?: string;
  /** ISO upper bound from the cache key; undefined when open-ended. */
  to?: string;
  /** Element 8 of the key. The default list fetches its Upcoming group
   *  ascending while that group is collapsed, so the order is part of a
   *  window's identity rather than a filter on it. */
  sortOrder: unknown;
  /** Rows in cached order, or undefined when the query is registered but has
   *  no data yet — which a caller must not read as "loaded and empty". */
  items?: SessionListItem[];
};

/** Element count of a `GET_SESSIONS_LIST` cache key (root plus ten inputs). */
const SESSIONS_LIST_KEY_LENGTH = 11;

/**
 * True for a cache key belonging to the *default* v2.2 list on `scope`: no
 * status, patient-profile, clinician, search or urgency filter, and the
 * default sort field. The date bounds (elements 3 and 4) and the sort order
 * (element 8) are deliberately not constrained — they are how the default
 * list's own per-date-group queries differ from one another, so they are
 * reported to the caller instead of filtered on here.
 */
function isDefaultSessionsListKey(key: readonly unknown[], scope: string): boolean {
  if (key.length !== SESSIONS_LIST_KEY_LENGTH) return false;
  const [
    root,
    keyScope,
    statuses,
    ,
    ,
    patientProfileIds,
    clinicianIds,
    sortBy,
    ,
    searchQuery,
    isUrgentOnly,
  ] = key;
  return (
    root === GET_SESSIONS_LIST &&
    keyScope === scope &&
    Array.isArray(statuses) &&
    statuses.length === 0 &&
    patientProfileIds === undefined &&
    clinicianIds === undefined &&
    sortBy === SESSION_LIST_DEFAULT_SORT_BY &&
    searchQuery === '' &&
    isUrgentOnly === false
  );
}

/**
 * Read the rows already cached for the default v2.2 session list on `scope`,
 * one entry per cached date-group query, WITHOUT subscribing (no fetch, no
 * polling). Returns an empty array when nothing matching is cached; an entry
 * with `items: undefined` is a query that exists but has not resolved.
 *
 * Deliberately raw: the rows come back in their cached order, grouped by the
 * date window they were fetched for, and selection semantics (ordering,
 * Upcoming handling, offline rows) stay with the app-side list that owns them.
 * Lets a non-scribe surface (the always-mounted sidebar) resolve the same
 * session the list's restore would open without mounting a list observer.
 */
export function getCachedDefaultSessionsListWindows(
  scope: string
): CachedSessionsListWindow[] {
  const queryClient = getSessionDataConfig().getQueryClient();

  return queryClient
    .getQueriesData<InfiniteData<SessionListResponse>>({
      queryKey: [GET_SESSIONS_LIST],
    })
    .filter(([key]) => isDefaultSessionsListKey(key, scope))
    .map(([key, data]) => ({
      from: typeof key[3] === 'string' ? key[3] : undefined,
      to: typeof key[4] === 'string' ? key[4] : undefined,
      sortOrder: key[8],
      items: data?.pages?.flatMap((page) => page.sessions ?? []),
    }));
}

/**
 * Hook that provides a function to cancel sessions query based on current feature flags.
 * Used in mutations to prevent race conditions during optimistic updates.
 * Handles V1, V2, and V3 query keys.
 *
 * The original read the page size through `useGetPageSize()` (a constant
 * returner staying app-side with `useQuerySessionsV2`); the package reads
 * the same `SESSIONS_PAGE_SIZE` contract constant directly — same value,
 * one fewer dep.
 */
export function useCancelSessionsQuery() {
  const queryClient = useQueryClient();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);

  const cancelSessionsQuery = useCallback(async () => {
    switch (sessionQueryVersion) {
      case 'v3':
        await queryClient.cancelQueries({
          queryKey: [GET_INFINITE_SESSIONS_WITH_PAGINATION],
        });
        break;
      case 'v2':
        await queryClient.cancelQueries({
          queryKey: [GET_INFINITE_SESSIONS, SESSIONS_PAGE_SIZE],
        });
        break;
      default:
        await queryClient.cancelQueries({
          queryKey: [GET_SESSIONS],
        });
    }
  }, [queryClient, sessionQueryVersion]);

  return { cancelSessionsQuery };
}

/**
 * Invalidate team member sessions query cache based on current feature flags.
 * Handles V1 and V2 query keys (V3 reuses V2's team member sessions).
 */
export function invalidateTeamMemberSessionsQuery(teamId?: string, memberId?: string) {
  const store = getDefaultStore();
  const sessionQueryVersion = store.get(sessionQueryVersionAtom);
  const queryClient = getSessionDataConfig().getQueryClient();

  switch (sessionQueryVersion) {
    case 'v3':
    case 'v2':
      // V2 and V3 use the same team member sessions query. Raw `100` in the
      // original — same frozen-pinned constant, same rationale as above.
      return queryClient.invalidateQueries({
        queryKey: [
          GET_INFINITE_TEAM_MEMBER_SESSIONS,
          { teamId },
          { memberId },
          SESSIONS_PAGE_SIZE,
        ],
      });
    default:
      // V1: Invalidate simple team member sessions query
      return queryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBER_SESSIONS, { teamId }, { memberId }],
      });
  }
}
